import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { AssetResponse } from '../../api';
import { listAssets } from '../../api';
import { SourceImageTile, useSourceImageTiles } from './SourceImageThumbs';
import { smallUrlOf, useAssetUrls } from './useAssetUrls';

/**
 * Below this many tiles the filter box is a control to ignore above pictures
 * you can already see in one glance.
 */
const FILTER_THRESHOLD = 6;

/**
 * How many tiles are rendered, and so how many display URLs get signed.
 *
 * There can be five hundred source images; there is no point signing five
 * hundred URLs — each a request, each expiring — to fill a three-column box
 * that shows nine at a time. Comfortably above `FILTER_THRESHOLD`, so the
 * filter box is already on screen by the time the cap can bite.
 */
const SHOWN_LIMIT = 24;

/**
 * The source image picker, shared by the drawer's lineage section and the
 * upload modal's OLA details step.
 *
 * Extracted from `SourceImageSection` because the upload modal needs the same
 * list and the same option labels but none of the mutations: at upload the
 * link is a field of the batch being created, not an action on existing rows.
 *
 * Published only, filtered by the server. Derivatives are always cut from a
 * published source image — a staged one is still triage and may be deleted —
 * and the backend refuses a link to anything else.
 *
 * With one exception: `linkedIds`, the sources the selection is already linked
 * to. A link written before only published sources could be may point at a
 * staged one, and leaving it out would show the asset as unlinked when it is
 * not. Those are fetched by id and kept, so the user sees the link and can
 * remove it — but nothing that is not already linked is ever offered.
 */
export function useSourceImageOptions(
  enabled = true,
  linkedIds: string[] = [],
): { options: AssetResponse[] } {
  const { data } = useQuery({
    queryKey: ['asset-library', 'key-visuals'],
    queryFn: () => listAssets({ asset_type: 'source_image', status: 'published', limit: 500 }),
    enabled,
  });
  // Only what the published list does not already cover, and only once it has
  // loaded — before then every linked id would look missing.
  const missing = data
    ? linkedIds.filter((id) => !data.assets.some((asset) => asset.id === id))
    : [];
  // Under `key-visuals` so that publishing, which invalidates that key, moves a
  // source from here into the published list instead of leaving it in both.
  const { data: linked } = useQuery({
    queryKey: ['asset-library', 'key-visuals', 'linked', missing.join(',')],
    queryFn: () => listAssets({ ids: missing.join(',') }),
    enabled: enabled && missing.length > 0,
  });
  const options = useMemo(() => {
    const published = data?.assets ?? [];
    const extra = (linked?.assets ?? []).filter(
      (asset) =>
        asset.asset_type === 'source_image' && !published.some((option) => option.id === asset.id),
    );
    return [...published, ...extra].sort(compareSourceImages);
  }, [data, linked]);
  return { options };
}

/**
 * Stable picker order: by source image code, then filename. The list endpoint
 * makes no ordering promise, and an unsorted 500-row `<select>` is unusable.
 * `localeCompare` with `numeric` keeps `SI-000009` ahead of `SI-000010` even
 * if codes ever lose their zero padding.
 */
function compareSourceImages(a: AssetResponse, b: AssetResponse): number {
  const byCode = (a.source_image_code ?? '').localeCompare(b.source_image_code ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  if (byCode !== 0) return byCode;
  return a.filename_original.localeCompare(b.filename_original, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function sourceImageOptionLabel(asset: AssetResponse): string {
  return `${asset.source_image_code} · ${asset.filename_original}${
    asset.status === 'staged' ? ' (staged)' : ''
  }`;
}

interface SourceImageSelectProps {
  /**
   * The chosen source images, in link order. Empty means "not linked".
   *
   * Ordered rather than a Set: `position` on the join rows comes from this
   * array, and that is what orders the thumbnail stack and decides which source
   * the deprecated single-valued compat fields report. A Set would make the
   * order an accident of insertion that React is free to re-render differently.
   */
  value: string[];
  onChange: (sourceImageIds: string[]) => void;
  options: AssetResponse[];
  disabled?: boolean;
  /** Paint the empty state amber: at least one link is required to publish. */
  required?: boolean;
  /** Label of the empty option. */
  emptyLabel?: string;
  /**
   * Restrict the control to a single source image. The upload form sets this:
   * a whole batch inherits the metadata of what it is linked to, and resolving
   * several sources' conflicting values across every file in the batch is a
   * question the upload step has no good way to ask.
   */
  single?: boolean;
  'aria-label'?: string;
}

/**
 * The picker, as pictures.
 *
 * It was a `<select>` of codes, which asked the user to answer "which image is
 * this?" from `SI-000042 · hero-front-3q.jpg` alone — and the honest answer is
 * that nobody knows. Showing the image only *after* picking is circular: you
 * have to already know the answer for the confirmation to tell you anything.
 * So the images are here, in the thing being chosen from.
 *
 * Checkboxes rather than radios, because an asset can be cut from several
 * source images — a banner composited from three shoots. The `single` variant
 * keeps radio semantics for the upload form, which genuinely does take one.
 *
 * Multi-select is a checkbox group and not a listbox for the same reason the
 * single version was a radio group: the native control brings focus order,
 * toggling and screen-reader semantics from the platform rather than from
 * several hundred lines of keyboard handling. The inputs stay in the DOM,
 * visually hidden, so the tile is a label and the control underneath is real.
 *
 * Picked tiles carry their ordinal, because with several sources the order is
 * information — it is the stacking order, and the first is what older screens
 * still show as "the" source image. A selection whose order is invisible is one
 * the user cannot deliberately get right.
 */
export function SourceImageSelect({
  value,
  onChange,
  options,
  disabled = false,
  required = false,
  emptyLabel = 'Not linked',
  single = false,
  'aria-label': ariaLabel,
}: SourceImageSelectProps) {
  const [filter, setFilter] = useState('');
  const { failedUrls, handleError, handleLoad } = useSourceImageTiles();

  // Position in the selection, for the ordinal badge. A map rather than
  // `indexOf` per tile: the grid re-renders on every keystroke in the filter
  // box, and this keeps that linear rather than quadratic.
  const orderById = useMemo(() => new Map(value.map((id, index) => [id, index])), [value]);

  // Appends rather than inserting, so the ordinal a tile is given when picked
  // is the one it keeps. Unpicking closes the gap — the remaining sources keep
  // their relative order and simply renumber, which is the only behaviour that
  // does not silently reshuffle a stack the user has already arranged.
  const toggle = (id: string) => {
    if (single) {
      onChange(value[0] === id ? [] : [id]);
      return;
    }
    onChange(value.includes(id) ? value.filter((chosen) => chosen !== id) : [...value, id]);
  };

  // Filtering is on code and filename, the two things actually written on a
  // tile. Only offered once the list is long enough to need it — below that it
  // is a box to ignore above six pictures you can already see.
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      sourceImageOptionLabel(option).toLowerCase().includes(needle),
    );
  }, [options, filter]);

  // Signed for what is on screen, not for the whole list. `useSourceImageOptions`
  // fetches up to five hundred heroes and this grid shows a handful of them at a
  // time, so signing by option would be five hundred URLs minted — and expiring —
  // to display maybe nine. The cap does that; filtering is how the user reaches
  // past it, which is why the box appears well below this number.
  //
  // Every current selection is kept, even when the cap or the filter would drop
  // it: a checkbox group whose checked options are not in the DOM reads as
  // nothing selected, and the user would be told they had unlinked something
  // they had not. All of them, not just the first — with several sources,
  // showing some of what is picked is worse than showing none, because it looks
  // like the complete answer.
  //
  // Selected tiles lead, in selection order, so the stack the user is building
  // stays together at the top instead of scattering through the grid as the
  // filter changes.
  const shown = useMemo(() => {
    const chosen = value
      .map((id) => options.find((option) => option.id === id))
      .filter((option): option is AssetResponse => option !== undefined);
    const rest = visible.filter((option) => !orderById.has(option.id));
    return [...chosen, ...rest.slice(0, Math.max(0, SHOWN_LIMIT - chosen.length))];
  }, [visible, options, value, orderById]);
  // Counted by identity, not by subtraction. A selected tile is shown even when
  // the filter does not match it, so it can be in `shown` without ever having
  // been in `visible` — subtracting the lengths then credits the filter with
  // tiles it never matched and under-reports what is still behind it. With
  // thirty matches and two selected non-matches, that read "6 more" when eight
  // matches were genuinely off screen.
  const hidden = useMemo(() => {
    const shownIds = new Set(shown.map((option) => option.id));
    return visible.filter((option) => !shownIds.has(option.id)).length;
  }, [visible, shown]);

  // The hook keys on the sorted id array, so a filter keystroke that does not
  // change the first tiles reuses the cached answer rather than re-signing.
  const ids = useMemo(() => shown.map((option) => option.id), [shown]);
  const urls = useAssetUrls(ids);

  return (
    <div
      // A checkbox group is not a radiogroup, and claiming otherwise makes a
      // screen reader announce "one of N" for a control where several are
      // legal. `group` is the honest container role; the count goes in the
      // label so the selection is audible without hunting through the tiles.
      role={single ? 'radiogroup' : 'group'}
      aria-label={
        ariaLabel ??
        (single || value.length === 0 ? 'Source image' : `Source images, ${value.length} selected`)
      }
      aria-required={required || undefined}
      className={`flex flex-col gap-1.5 rounded-md border p-1.5 ${
        value.length > 0 || !required ? 'border-slate-200' : 'border-amber-300 bg-amber-50/40'
      } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      {options.length > FILTER_THRESHOLD && (
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by code or filename"
          aria-label="Filter source images"
          className="rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-800 outline-none focus:border-[#0014cc]"
        />
      )}

      <div className="grid max-h-56 grid-cols-3 gap-1.5 overflow-y-auto">
        {/* Unlinking is a choice like any other, so it is a tile in the grid
            rather than a separate control the user has to go looking for.
            A button when several are allowed: "none" is not a peer of the
            other checkboxes — it is the act of clearing them — and a checkbox
            that unticks other checkboxes is a control that lies about itself. */}
        {single ? (
          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border p-1 text-center transition-colors ${
              value.length === 0
                ? 'border-[#0014cc] bg-[#0014cc]/5'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              className="sr-only"
              // Named explicitly rather than left to the surrounding text: the
              // drawer wraps this group in a `<label>` of its own, which would
              // otherwise prepend the section's name to every tile and make all
              // of them read alike.
              //
              // Not `emptyLabel`: that is sometimes an em-dash, which is a fine
              // thing to show and a useless thing to hear. The visible label and
              // the spoken one differ here on purpose.
              aria-label="No source image"
              checked={value.length === 0}
              disabled={disabled}
              onChange={() => onChange([])}
            />
            <span className="flex h-14 w-full items-center justify-center rounded bg-slate-50 text-[10px] font-medium text-slate-400">
              None
            </span>
            <span className="w-full truncate text-[10px] text-slate-500">{emptyLabel}</span>
          </label>
        ) : (
          <button
            type="button"
            aria-label="Clear all source images"
            disabled={disabled || value.length === 0}
            onClick={() => onChange([])}
            className="flex flex-col items-center justify-center gap-1 rounded-md border border-slate-200 p-1 text-center transition-colors hover:border-slate-300 disabled:cursor-default disabled:opacity-50 disabled:hover:border-slate-200"
          >
            <span className="flex h-14 w-full items-center justify-center rounded bg-slate-50 text-[10px] font-medium text-slate-400">
              None
            </span>
            <span className="w-full truncate text-[10px] text-slate-500">
              {value.length === 0 ? emptyLabel : 'Clear all'}
            </span>
          </button>
        )}

        {shown.map((option) => {
          const url = smallUrlOf(urls, option.id);
          const order = orderById.get(option.id);
          const isSelected = order !== undefined;
          return (
            <label
              key={option.id}
              title={sourceImageOptionLabel(option)}
              className={`relative flex cursor-pointer flex-col items-center gap-1 rounded-md border p-1 text-center transition-colors ${
                isSelected
                  ? 'border-[#0014cc] bg-[#0014cc]/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type={single ? 'radio' : 'checkbox'}
                className="sr-only"
                aria-label={sourceImageOptionLabel(option)}
                checked={isSelected}
                disabled={disabled}
                onChange={() => toggle(option.id)}
              />
              <SourceImageTile
                url={url}
                code={option.source_image_code ?? undefined}
                filename={option.filename_original}
                size="lg"
                isBroken={url !== undefined && failedUrls.has(url)}
                onError={() => url && handleError(option.id, url, urls.refresh)}
                onLoad={() => handleLoad(option.id)}
                className="rounded"
              />
              {/* The ordinal, only where order is a thing the user can control.
                  Hidden from screen readers: the position is already in the
                  group's own label and in the summary under the grid, and a
                  bare "2" read out mid-tile is noise. */}
              {isSelected && !single && (
                <span
                  aria-hidden="true"
                  className="absolute left-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#0014cc] text-[9px] font-semibold leading-none text-white"
                >
                  {(order ?? 0) + 1}
                </span>
              )}
              <span
                className={`w-full truncate text-[10px] font-medium ${
                  isSelected ? 'text-[#0014cc]' : 'text-slate-600'
                }`}
              >
                {option.source_image_code}
                {/* Only an existing link can be staged; see `useSourceImageOptions`. */}
                {option.status === 'staged' && (
                  <span className="ml-1 font-normal text-amber-600">staged</span>
                )}
              </span>
            </label>
          );
        })}

        {visible.length === 0 && (
          <p className="col-span-3 px-1 py-3 text-center text-[11.5px] text-slate-400">
            No source image matches “{filter}”.
          </p>
        )}

        {/* Said rather than silently truncated: a grid that simply stops looks
            like the whole list, and the user would filter only by luck. */}
        {hidden > 0 && (
          <p className="col-span-3 px-1 py-2 text-center text-[11px] text-slate-400">
            {hidden} more — filter to narrow the list.
          </p>
        )}
      </div>

      {/* The selection in words, because the grid scrolls and the ordinals can
          be off-screen. `aria-live` so picking a tile is confirmed audibly —
          the checkbox announces itself, but not what it did to the order. */}
      {!single && value.length > 1 && (
        <p aria-live="polite" className="px-0.5 text-[11px] text-slate-500">
          {value.length} source images — stacked in the order picked.
        </p>
      )}
    </div>
  );
}
